import {
  IDENTITY_DOCUMENT_TYPE_LABELS,
  IDENTITY_DOCUMENT_TYPES,
  identityFormType,
  IdentityDocumentType,
  IdentityVerificationRow,
} from "../../../api/verificationsV2";
import { IColumnConfig } from "../../../components/ui/baseDataTable";
import SelectCell from "../../../components/ui/baseDataTable/cells/SelectCell";
import { ANOMALY_SELECT_OPTIONS } from "../../legalTechnicalAnalysis/components/anomalyOptions";
import FormConfigCell from "../../legalTechnicalAnalysis/components/FormConfigCell";

import DocumentIdCell from "./DocumentIdCell";

interface IdentityVerificationColumnsProps {
  rows: IdentityVerificationRow[];
  stakeholderId: string;
  resolveDocumentId: (documentType?: IdentityDocumentType) => string | undefined;
  onRowChange: (
    rowIndex: number,
    value: any,
    field: keyof IdentityVerificationRow,
  ) => void;
}

const getAvailableDocumentTypes = (
  rows: IdentityVerificationRow[],
  currentRow: IdentityVerificationRow,
) => {
  const usedTypes = rows
    .filter((row) => row.verification_id !== currentRow.verification_id)
    .map((row) => row.document_type);

  return IDENTITY_DOCUMENT_TYPES
    .filter((documentType) => !usedTypes.includes(documentType))
    .map((documentType) => ({
      name: IDENTITY_DOCUMENT_TYPE_LABELS[documentType],
      value: documentType,
    }));
};

export const buildIdentityVerificationColumns = ({
  rows,
  stakeholderId,
  resolveDocumentId,
  onRowChange,
}: IdentityVerificationColumnsProps): IColumnConfig<IdentityVerificationRow>[] => [
  {
    headerName: "Document Type",
    field: "document_type",
    type: "custom",
    getValue: (row) => row.document_type ?? "",
    isEditable: (_, row) => row.isAdded,
    hideFocus: (row) => !row.isAdded,
    render: (rowIndex, value) => {
      const row = rows[rowIndex];

      return (
        <div className="min-w-[160px]">
          <SelectCell
            name="document type"
            value={value as string}
            isEditable={row.isAdded}
            options={getAvailableDocumentTypes(rows, row)}
            className="px-0"
            labelClassName="px-0 py-1 text-xs w-full"
            onChange={(updated) => onRowChange(rowIndex, updated, "document_type")}
          />
        </div>
      );
    },
  },
  {
    headerName: "Document ID",
    field: "document_entity_id",
    type: "custom",
    getValue: (row) => row.document_entity_id ?? "",
    isEditable: false,
    render: (rowIndex) => {
      const row = rows[rowIndex];

      return (
        <DocumentIdCell
          stakeholderId={stakeholderId}
          documentType={row.document_type}
          documentId={resolveDocumentId(row.document_type)}
        />
      );
    },
  },
  {
    headerName: "Onboarding Status",
    field: "onboarding_details",
    type: "custom",
    getValue: (row) => row.onboarding_details,
    isEditable: true,
    render: (rowIndex, value) => {
      const row = rows[rowIndex];

      return (
        <FormConfigCell
          title="Onboarding Status"
          formType={
            row.document_type
              ? identityFormType(row.document_type)
              : undefined
          }
          disabled={!row.document_type}
          values={value ?? undefined}
          onChange={(updated) =>
            onRowChange(rowIndex, updated, "onboarding_details")
          }
        />
      );
    },
  },
  {
    headerName: "Current Status",
    field: "current_details",
    type: "custom",
    getValue: (row) => row.current_details,
    isEditable: true,
    render: (rowIndex, value) => {
      const row = rows[rowIndex];

      return (
        <FormConfigCell
          title="Current Status"
          formType={
            row.document_type
              ? identityFormType(row.document_type)
              : undefined
          }
          disabled={!row.document_type}
          values={value ?? undefined}
          onChange={(updated) =>
            onRowChange(rowIndex, updated, "current_details")
          }
        />
      );
    },
  },
  {
    headerName: "Anomaly",
    field: "anomaly",
    type: "select",
    getValue: (row) => row.anomaly ?? "",
    isEditable: (_, row) => Boolean(row.document_type),
    hideFocus: (row) => !row.document_type,
    onChange: onRowChange,
    options: ANOMALY_SELECT_OPTIONS,
    selectCellProps: {
      placeholder: "Anomaly",
      className: "px-0",
      rootContainerClassName: "min-w-[90px] flex items-center",
      labelClassName: "px-0 py-1 text-xs min-w-[90px]",
    },
  },
];
